<script setup lang="ts">
import { computed } from 'vue';
import type { RendererZone, ResolvedBinding } from './types';
import { urlFrameSource } from './urlEmbed';
import Clock from './Clock.vue';
import ProgrammeZone from './ProgrammeZone.vue';

const props = defineProps<{
  zone: RendererZone;
  binding: ResolvedBinding;
  /**
   * Fixed instant for programme zones. Omit for a live clock; a preview that
   * wants to show a particular evening passes one.
   */
  now?: Date | undefined;
}>();

const emit = defineEmits<{
  warn: [message: string];
}>();

/**
 * The media zone's `defaultFit` parameter, defaulting to cover. Read leniently:
 * the parameters JSON is API-validated on write, so an unreadable value means
 * an older payload, not an error worth blanking a zone over.
 */
const mediaFit = computed<'cover' | 'contain' | 'fill'>(() => {
  const fit = props.zone.parameters?.defaultFit;
  return fit === 'contain' || fit === 'fill' ? fit : 'cover';
});

function onMediaError(event: Event): void {
  const t = event.target as HTMLImageElement | HTMLVideoElement;
  const src = (t as HTMLVideoElement).currentSrc ?? (t as HTMLImageElement).src;
  emit('warn', `media-load-failed:${src.slice(0, 80)}`);
}

/**
 * YouTube page URLs refuse framing and are rewritten to the embed player
 * here, with the sandbox loosened to what that player needs; every other
 * URL passes through with its profile's tokens.
 */
const urlFrame = computed(() =>
  props.binding.kind === 'url'
    ? urlFrameSource(props.binding.src, props.binding.sandboxProfile)
    : null,
);

function onUrlFrameLoad(event: Event): void {
  // Heuristic: cross-origin pages blocked by X-Frame-Options / frame-ancestors
  // typically expose an empty contentWindow.length. We can't read most of the
  // iframe across origins, but this single property is permitted and serves
  // as a best-effort blocked-frame signal.
  const t = event.target as HTMLIFrameElement;
  try {
    if (t.contentWindow && t.contentWindow.length === 0) {
      // length===0 also happens for legitimate pages with no subframes; the
      // false-positive cost is "a warning fires too often". Surfacing the
      // signal in heartbeat telemetry is the operator's diagnostic, not a
      // hard failure, so the trade-off is acceptable for now.
      emit('warn', `url-frame-blocked:${t.src.slice(0, 80)}`);
    }
  } catch {
    /* cross-origin denied: no signal here */
  }
}
</script>

<template>
  <div
    class="lr42-zone"
    :style="{
      left: `${zone.x}%`,
      top: `${zone.y}%`,
      width: `${zone.width}%`,
      height: `${zone.height}%`,
      zIndex: zone.zIndex ?? 0,
    }"
  >
    <template v-if="binding.kind === 'media'">
      <video
        v-if="binding.mimeType.startsWith('video/')"
        :src="binding.src"
        muted
        autoplay
        loop
        playsinline
        class="lr42-media"
        :style="{ objectFit: mediaFit }"
        @error="onMediaError"
      />
      <iframe
        v-else-if="binding.mimeType === 'text/html'"
        :src="binding.src"
        class="lr42-frame"
        sandbox="allow-scripts"
        referrerpolicy="no-referrer"
      />
      <img
        v-else
        :src="binding.src"
        alt=""
        class="lr42-media"
        :style="{ objectFit: mediaFit }"
        @error="onMediaError"
      />
    </template>
    <iframe
      v-else-if="binding.kind === 'url' && urlFrame"
      :src="urlFrame.src"
      class="lr42-frame lr42-frame--url"
      :class="{ 'lr42-frame--interactive': binding.sandboxProfile === 'interactive' }"
      :sandbox="urlFrame.sandbox"
      :scrolling="binding.sandboxProfile === 'interactive' ? 'auto' : 'no'"
      allow="autoplay; fullscreen"
      :referrerpolicy="urlFrame.referrerPolicy"
      loading="eager"
      @load="onUrlFrameLoad"
    />
    <div v-else-if="binding.kind === 'text'" class="lr42-text">{{ binding.text }}</div>
    <ProgrammeZone
      v-else-if="binding.kind === 'programme'"
      :params="binding.params"
      :payload="binding.payload"
      :strings="binding.strings"
      :now="now"
      :zone-name="zone.name"
      @warn="(m: string) => emit('warn', m)"
    />
    <Clock
      v-else-if="binding.kind === 'clock'"
      :params="binding.params"
      :now="now"
      @warn="(m: string) => emit('warn', m)"
    />
    <div v-else-if="binding.kind === 'unavailable'" class="lr42-placeholder lr42-placeholder--warn">
      {{ binding.label }}
    </div>
    <div v-else-if="binding.kind === 'placeholder'" class="lr42-placeholder">
      {{ binding.label }}
    </div>
  </div>
</template>

<style scoped>
.lr42-zone {
  position: absolute;
  overflow: hidden;
}
.lr42-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.lr42-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.lr42-frame--url {
  outline: 0;
  background: transparent;
}
/* The player's transient-zone layers carry `pointer-events: none` so an
   outgoing layer can't swallow taps meant for the continuous zones underneath
   it, and a url frame inherits that — which on a touch screen means no click
   ever reaches the embedded site. The `interactive` sandbox profile is the one
   thing on a board that is *supposed* to take input, so it re-enables hit
   testing for itself; `strict` stays inert, which is what a display-only
   embed wants. */
.lr42-frame--interactive {
  pointer-events: auto;
}
.lr42-text {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  font-family: var(--lr42-text-zone-font, Georgia, 'Times New Roman', serif);
  font-size: var(--lr42-text-zone-size, clamp(24px, 6vw, 96px));
  color: var(--lr42-text-zone-color, #f6eedc);
  text-align: center;
  padding: 16px;
}
.lr42-placeholder {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(246, 238, 220, 0.4);
  background: rgba(246, 238, 220, 0.03);
  border: 1px dashed rgba(246, 238, 220, 0.16);
  padding: 8px;
  text-align: center;
}
.lr42-placeholder--warn {
  color: rgba(245, 184, 160, 0.7);
  border-color: rgba(245, 184, 160, 0.3);
  background: rgba(245, 184, 160, 0.05);
}
</style>
